import {
  FinancialCashOverview,
  FinancialPage,
  FinancialSection,
  readFinancialCashOverview,
  readFinancialPage,
} from './FinancialReadService';
import {
  FinancialMigrationControl,
  readFinancialMigrationControl,
} from './FinancialMigrationControlService';
import { supabase } from '../config/supabase';

export interface FinancialReconciliationSection {
  section: Extract<FinancialSection, 'patients' | 'contracts' | 'packages' | 'invoices' | 'settlements'>;
  total: number;
  sampleCount: number;
}

export interface FinancialReconciliationSnapshot {
  executedAt: string;
  periodFrom: string;
  periodUntil: string;
  control: FinancialMigrationControl;
  sections: FinancialReconciliationSection[];
  historicalComparisons: FinancialHistoricalComparison[];
  cash: FinancialCashOverview;
  cashDifference: number;
}

export interface FinancialHistoricalComparison {
  key: 'patients' | 'contracts' | 'packages' | 'invoices';
  label: string;
  legacyLabel: string;
  legacyTotal: number;
  mappedNewTotal: number;
  newTotal: number;
  newAdditionalTotal: number;
  difference: number;
  note: string | null;
}

const sections: FinancialReconciliationSection['section'][] = [
  'patients',
  'contracts',
  'packages',
  'invoices',
  'settlements',
];

function monthBounds(month: string): { from: string; until: string } {
  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return { from: `${month}-01`, until: `${month}-${String(lastDay).padStart(2, '0')}` };
}

export async function runFinancialReadReconciliation(
  clientId: string,
  month = new Date().toISOString().slice(0, 7),
): Promise<FinancialReconciliationSnapshot> {
  const control = await readFinancialMigrationControl(clientId, 'a8');
  if (control.mode !== 'read' || control.status !== 'enabled') {
    throw new Error('A reconciliação somente leitura exige que o piloto A8 esteja em modo read e habilitado.');
  }

  const bounds = monthBounds(month);
  const readAll = async (section: FinancialReconciliationSection['section']): Promise<FinancialPage> => {
    const first = await readFinancialPage(clientId, section, null, '', 0, new AbortController().signal);
    const records = [...first.records];
    while (records.length < first.total) {
      const next = await readFinancialPage(clientId, section, null, '', records.length, new AbortController().signal);
      records.push(...next.records);
      if (next.records.length === 0) break;
    }
    return { ...first, records };
  };

  const [pages, cash] = await Promise.all([
    Promise.all(sections.map(readAll)),
    readFinancialCashOverview(clientId, bounds.from, bounds.until),
  ]);

  const readRows = async (table: string, columns: string) => {
    const { data, error } = await supabase.from(table).select(columns).eq('clientId', clientId).returns<Array<Record<string, unknown>>>();
    if (error) throw new Error(`Não foi possível consultar a fonte histórica ${table}: ${error.message}`);
    return (data ?? []) as Array<Record<string, unknown>>;
  };

  const [legacyPatients, legacyProfiles, legacyInvoices, legacyPackages] = await Promise.all([
    readRows('patient_clients', 'patientId'),
    readRows('financial_profiles', 'patientId'),
    readRows('invoices', 'patientId'),
    readRows('packages', 'packageId'),
  ]);

  const newContracts = pages[1].records;
  const newPackages = pages[2].records;
  const newInvoices = pages[3].records;

  const newTotals = new Map(pages.map((page, index) => [sections[index], page.total]));
  const legacyContractPatients = new Set([...legacyProfiles, ...legacyInvoices].map((row) => String(row.patientId ?? '')).filter(Boolean));
  const mappedContractPatients = new Set(newContracts
    .filter((row) => String((row.contractAdditionalRules as Record<string, unknown> | null)?.source ?? '') === 'legacy_financial_profile_or_invoice')
    .map((row) => String(row.patientId ?? ''))
    .filter(Boolean));
  const allNewContractPatients = new Set(newContracts.map((row) => String(row.patientId ?? '')).filter(Boolean));
  const legacyPatientsWithAnyContract = new Set([...allNewContractPatients].filter((patientId) => legacyContractPatients.has(patientId)));
  const unmarkedContractPatientNames = newContracts
    .filter((row) => {
      const patientId = String(row.patientId ?? '');
      return legacyContractPatients.has(patientId) && !mappedContractPatients.has(patientId);
    })
    .map((row) => String(row.patientName ?? row.displayName ?? 'Paciente não identificado'))
    .filter((value, index, values) => values.indexOf(value) === index);
  const contractNote = mappedContractPatients.size === legacyContractPatients.size
    ? null
      : legacyPatientsWithAnyContract.size === legacyContractPatients.size
      ? `${legacyContractPatients.size - mappedContractPatients.size} paciente(s) legado(s) de referência possuem contrato novo, mas não estão marcado(s) com a origem técnica esperada. Paciente(s): ${unmarkedContractPatientNames.join(', ')}.`
      : `${legacyContractPatients.size - legacyPatientsWithAnyContract.size} paciente(s) legado(s) não possuem contrato novo correspondente.`;
  const historicalComparisons: FinancialHistoricalComparison[] = [
    {
      key: 'patients', label: 'Pacientes', legacyLabel: 'patient_clients',
      legacyTotal: legacyPatients.length, mappedNewTotal: newTotals.get('patients') ?? 0,
      newTotal: newTotals.get('patients') ?? 0, newAdditionalTotal: 0,
      difference: (newTotals.get('patients') ?? 0) - legacyPatients.length,
      note: null,
    },
    {
      key: 'contracts', label: 'Contratos (pacientes de referência)', legacyLabel: 'financial_profiles + invoices (pacientes únicos)',
      legacyTotal: legacyContractPatients.size, mappedNewTotal: mappedContractPatients.size,
      newTotal: newTotals.get('contracts') ?? 0,
      newAdditionalTotal: Math.max(0, (newTotals.get('contracts') ?? 0) - mappedContractPatients.size),
      difference: mappedContractPatients.size - legacyContractPatients.size,
      note: contractNote,
    },
    {
      key: 'packages', label: 'Pacotes', legacyLabel: 'packages',
      legacyTotal: legacyPackages.length,
      mappedNewTotal: newPackages.filter((row) => row.legacyPackageId != null).length,
      newTotal: newTotals.get('packages') ?? 0,
      newAdditionalTotal: Math.max(0, (newTotals.get('packages') ?? 0) - newPackages.filter((row) => row.legacyPackageId != null).length),
      difference: newPackages.filter((row) => row.legacyPackageId != null).length - legacyPackages.length,
      note: null,
    },
    {
      key: 'invoices', label: 'Faturas', legacyLabel: 'invoices',
      legacyTotal: legacyInvoices.length,
      mappedNewTotal: newInvoices.filter((row) => row.legacyInvoiceId != null).length,
      newTotal: newTotals.get('invoices') ?? 0,
      newAdditionalTotal: Math.max(0, (newTotals.get('invoices') ?? 0) - newInvoices.filter((row) => row.legacyInvoiceId != null).length),
      difference: newInvoices.filter((row) => row.legacyInvoiceId != null).length - legacyInvoices.length,
      note: null,
    },
  ];

  const totals = cash.totals;
  const cashDifference = Number((totals.openingBalance + totals.inflow - totals.outflow - totals.closingBalance).toFixed(2));

  return {
    executedAt: new Date().toISOString(),
    periodFrom: bounds.from,
    periodUntil: bounds.until,
    control,
    sections: pages.map((page: FinancialPage, index) => ({
      section: sections[index],
      total: page.total,
      sampleCount: page.records.length,
    })),
    historicalComparisons,
    cash,
    cashDifference,
  };
}
